import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BookOpen, PenTool, Notebook, Flag, Search, Plus, Trash2, Save, Sparkles, Lock, FileText, X, Copy, Check, Play } from "lucide-react";
import Simulator from "./Simulator";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { key: "rulebook", label: "Rulebook", icon: BookOpen },
  { key: "drafter", label: "Rules Drafter", icon: PenTool },
  { key: "simulator", label: "Playtest Simulator", icon: Play },
  { key: "playtest", label: "Playtest Log", icon: Notebook },
  { key: "still-open", label: "Still Open", icon: Flag },
];

function App() {
  const [tab, setTab] = useState("rulebook");
  const [modules, setModules] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [stillOpen, setStillOpen] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [splashDone, setSplashDone] = useState(false);
  const [splashFading, setSplashFading] = useState(false);

  const loadAll = async () => {
    const [m, s, so, rem] = await Promise.all([
      axios.get(`${API}/modules`),
      axios.get(`${API}/sessions`),
      axios.get(`${API}/still-open`),
      axios.get(`${API}/reminders`),
    ]);
    setModules(m.data);
    setSessions(s.data);
    setStillOpen(so.data);
    setReminders(rem.data);
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  // Splash stays until the user chooses to enter (proper title-screen behaviour).
  // While data is still loading we show "Woofing…"; once loaded we invite them in.
  const dismissSplash = () => {
    setSplashFading(true);
    setTimeout(() => setSplashDone(true), 480);
  };

  return (
    <div className="App min-h-screen">
      {!splashDone && <WoofingSplash fading={splashFading} ready={!loading} onEnter={dismissSplash} />}
      <Header />
      <ReminderBanner reminders={reminders} />
      <nav className="max-w-7xl mx-auto px-6 border-b border-[color:var(--border-brass)]" data-testid="desk-header">
        <div className="flex items-center gap-8 overflow-x-auto">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`tab-btn ${tab === key ? "active" : ""} flex items-center gap-2`}
              data-testid={`tab-${key}`}
            >
              <Icon size={13} />
              {label}
              {key === "still-open" && stillOpen.length > 0 && (
                <span className="ml-1 crimson-tag" data-testid="still-open-count">{stillOpen.length}</span>
              )}
            </button>
          ))}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-20 eyebrow">Loading the case files…</div>
        ) : (
          <>
            {tab === "rulebook" && <RulebookView modules={modules} onReload={loadAll} />}
            {tab === "drafter" && <DrafterView onSaved={loadAll} />}
            {tab === "simulator" && <Simulator />}
            {tab === "playtest" && <PlaytestView sessions={sessions} onReload={loadAll} />}
            {tab === "still-open" && <StillOpenView items={stillOpen} />}
          </>
        )}
      </main>

      <footer className="max-w-7xl mx-auto px-6 py-10 mt-8 border-t border-[color:var(--border-brass)] text-center">
        <div className="eyebrow" data-testid="footer-motto">Est. Case 001 · Dinner Inquiry in Progress</div>
      </footer>
    </div>
  );
}

function ReminderBanner({ reminders }) {
  if (!reminders?.length) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const items = reminders.map(r => {
    const due = new Date(r.due_at);
    const days = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    const urgent = days <= (r.warn_days ?? 7);
    const overdue = days < 0;
    return { r, days, urgent, overdue };
  });
  const anyUrgent = items.some(i => i.urgent || i.overdue);
  return (
    <div className="max-w-7xl mx-auto px-6 pt-3" data-testid="reminder-banner">
      {items.map(({ r, days, urgent, overdue }) => (
        <div
          key={r.id}
          className="mb-2 rounded-md px-4 py-2 flex items-center justify-between flex-wrap gap-3"
          style={{
            background: overdue || urgent
              ? "linear-gradient(90deg, rgba(166,50,50,0.20), rgba(140,34,34,0.10))"
              : "linear-gradient(90deg, rgba(197,160,89,0.10), rgba(20,14,10,0.6))",
            border: `1px solid ${overdue || urgent ? "var(--crimson)" : "var(--border-brass)"}`,
            animation: overdue || urgent ? "reminderPulse 2200ms ease-in-out infinite" : "none",
          }}
          data-testid={`reminder-${r.kind}`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className={overdue || urgent ? "crimson-tag" : "brass-tag"}>
              {overdue ? "Overdue" : urgent ? "Renew Soon" : "Upcoming"}
            </span>
            <span className="serif" style={{ color: "#F0E6D2", fontSize: "15px" }}>
              {r.label}
            </span>
            <span className="mono text-xs" style={{ color: "var(--muted)" }}>
              · due {r.due_at}
            </span>
          </div>
          <span className="serif font-bold" style={{ color: overdue || urgent ? "#E88A8A" : "var(--brass)" }}>
            {overdue ? `${Math.abs(days)} days overdue` : days === 0 ? "due today" : `${days} days left`}
          </span>
        </div>
      ))}
      {anyUrgent && (
        <style>{`
          @keyframes reminderPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(166, 50, 50, 0.0); }
            50%      { box-shadow: 0 0 22px 2px rgba(166, 50, 50, 0.35); }
          }
        `}</style>
      )}
    </div>
  );
}

function WoofingSplash({ fading, ready, onEnter }) {
  return (
    <div
      className={`splash-fixed ${fading ? "splash-fade-out" : ""}`}
      data-testid="woofing-splash"
      onClick={ready ? onEnter : undefined}
      style={{ cursor: ready ? "pointer" : "wait" }}
    >
      <div className="splash-frame" data-testid="woofing-image-frame">
        <img
          src="https://customer-assets-4nw71qhi.emergentagent.net/wingman/29a08ca2-646d-47e4-9635-c5bf0565e15e/attachments/498a3ac0aaf2499fb05e000295d91bcb_Picsart_26-09-22_05-19-21-636.jpg"
          alt="Blueprint Bones — a German Shepherd, a teacup of dominoes, and a rondel of clues"
        />
      </div>
      {ready ? (
        <button
          className="brass-btn brass-btn-solid"
          onClick={(e) => { e.stopPropagation(); onEnter(); }}
          data-testid="enter-btn"
          style={{ fontSize: "13px", padding: "12px 28px", letterSpacing: "0.24em" }}
        >
          Enter the Inquiry
        </button>
      ) : (
        <div className="woofing-text" data-testid="woofing-text">
          Woofing<span className="woofing-dots"><span>.</span><span>.</span><span>.</span></span>
        </div>
      )}
      <div className="mono text-xs" style={{ color: "var(--muted)", letterSpacing: "0.2em" }} data-testid="splash-hint">
        {ready ? "tap anywhere to begin" : "loading the case files"}
      </div>
    </div>
  );
}

function Header() {
  return (
    <div className="velvet">
      <header className="lamp-glow max-w-7xl mx-auto px-6 pt-14 pb-10 fade-up">
        <div className="eyebrow mb-3" style={{ color: "#F0B84E" }}>Case File Companion · Detective's Desk</div>
        <h1 className="serif text-4xl md:text-6xl font-bold tracking-tight" style={{ color: "#F5E7C2", textShadow: "0 2px 24px rgba(0,0,0,0.5), 0 0 40px rgba(230,161,53,0.15)" }}>
          The Dinner Inquiry
        </h1>
        <p className="serif-italic text-lg md:text-xl mt-3" style={{ color: "#F0D9AA", maxWidth: "600px" }}>
          A designer's manuscript desk — rulebook, drafter, playtest ledger, and the pinboard of everything still open.
        </p>
      </header>
    </div>
  );
}

// ============ RULEBOOK ============
function RulebookView({ modules, onReload }) {
  const [activeSlug, setActiveSlug] = useState(modules[0]?.slug);
  const [search, setSearch] = useState("");
  const active = useMemo(() => modules.find(m => m.slug === activeSlug) || modules[0], [modules, activeSlug]);

  const filteredModules = useMemo(() => {
    if (!search.trim()) return modules;
    const q = search.toLowerCase();
    return modules.filter(m =>
      m.title.toLowerCase().includes(q) ||
      m.content.toLowerCase().includes(q) ||
      (m.still_open || []).some(s => s.toLowerCase().includes(q))
    );
  }, [modules, search]);

  useEffect(() => {
    if (!activeSlug && modules[0]) setActiveSlug(modules[0].slug);
  }, [modules, activeSlug]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 fade-up" data-testid="rulebook-module-viewer">
      <aside className="leather rounded-md p-4 h-fit lg:sticky lg:top-6">
        <div className="relative mb-4">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--brass-dim)" }} />
          <input
            className="input-field pl-9"
            placeholder="Search modules…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="rulebook-search-input"
          />
        </div>
        <div className="eyebrow mb-2 px-3">Modules</div>
        <div className="flex flex-col">
          {filteredModules.map(m => (
            <button
              key={m.slug}
              className={`nav-item ${active?.slug === m.slug ? "active" : ""}`}
              onClick={() => setActiveSlug(m.slug)}
              data-testid={`module-nav-${m.slug}`}
            >
              <span className={`status-dot ${m.status}`} />
              <span className="truncate">{m.title}</span>
            </button>
          ))}
        </div>
      </aside>

      {active && <ModulePane key={active.slug} module={active} onReload={onReload} />}
    </div>
  );
}

function ModulePane({ module, onReload }) {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(module.content);
  const [title, setTitle] = useState(module.title);
  const [stillOpenText, setStillOpenText] = useState((module.still_open || []).join("\n"));
  const [status, setStatus] = useState(module.status);

  useEffect(() => {
    setContent(module.content);
    setTitle(module.title);
    setStillOpenText((module.still_open || []).join("\n"));
    setStatus(module.status);
    setEditing(false);
  }, [module.slug]);

  const save = async () => {
    await axios.patch(`${API}/modules/${module.slug}`, {
      title,
      content,
      status,
      still_open: stillOpenText.split("\n").map(s => s.trim()).filter(Boolean),
    });
    await onReload();
    setEditing(false);
  };

  const del = async () => {
    if (!window.confirm(`Remove "${module.title}" from the rulebook?`)) return;
    await axios.delete(`${API}/modules/${module.slug}`);
    await onReload();
  };

  return (
    <article className="paper rounded-md p-8 md:p-12 fade-up delay-1" data-testid="rulebook-active-module">
      <div className="flex items-start justify-between gap-4 mb-1 flex-wrap">
        <div>
          <div className="eyebrow" style={{ color: "var(--crimson-deep)" }}>{module.subtitle || "Rulebook Module"}</div>
          {editing ? (
            <input
              className="input-field mt-2 serif text-3xl"
              style={{ background: "rgba(197,160,89,0.08)", color: "var(--paper-ink)", borderColor: "var(--border-paper)" }}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="module-title-input"
            />
          ) : (
            <h2 className="serif text-3xl md:text-4xl font-bold mt-1" style={{ color: "var(--paper-ink)" }} data-testid="module-title-display">
              {module.title}
            </h2>
          )}
          <div className="flex items-center gap-2 mt-3">
            <span className={module.status === "locked" ? "brass-tag" : "crimson-tag"} data-testid="module-status">
              {module.status === "locked" ? <><Lock size={10} /> Locked Draft</> : <><FileText size={10} /> Draft</>}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <button className="brass-btn-solid brass-btn" onClick={save} data-testid="save-module-btn">
                <Save size={12} className="inline mr-1" /> Save
              </button>
              <button className="brass-btn" onClick={() => setEditing(false)} data-testid="cancel-edit-btn">Cancel</button>
            </>
          ) : (
            <>
              <button className="brass-btn" onClick={() => setEditing(true)} data-testid="edit-module-btn">Edit</button>
              <button className="brass-btn" onClick={del} data-testid="delete-module-btn" style={{ color: "var(--crimson)" }}>
                <Trash2 size={12} />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="ornament-divider"><span>❦</span></div>

      {editing ? (
        <div className="space-y-4">
          <div>
            <label className="eyebrow block mb-1" style={{ color: "var(--crimson-deep)" }}>Status</label>
            <select
              className="input-field"
              style={{ background: "rgba(197,160,89,0.08)", color: "var(--paper-ink)", borderColor: "var(--border-paper)" }}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="module-status-select"
            >
              <option value="draft">Draft</option>
              <option value="locked">Locked</option>
            </select>
          </div>
          <div>
            <label className="eyebrow block mb-1" style={{ color: "var(--crimson-deep)" }}>Content (Markdown)</label>
            <textarea
              className="input-field font-mono"
              style={{ background: "rgba(197,160,89,0.06)", color: "var(--paper-ink)", borderColor: "var(--border-paper)", minHeight: "500px" }}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              data-testid="module-content-textarea"
            />
          </div>
          <div>
            <label className="eyebrow block mb-1" style={{ color: "var(--crimson-deep)" }}>Still Open (one per line)</label>
            <textarea
              className="input-field font-mono"
              style={{ background: "rgba(197,160,89,0.06)", color: "var(--paper-ink)", borderColor: "var(--border-paper)", minHeight: "120px" }}
              value={stillOpenText}
              onChange={(e) => setStillOpenText(e.target.value)}
              data-testid="module-still-open-textarea"
            />
          </div>
        </div>
      ) : (
        <>
          <div className="prose-paper" data-testid="module-rendered-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{module.content}</ReactMarkdown>
          </div>

          {(module.still_open || []).length > 0 && (
            <div className="mt-8 p-5 rounded-r-md" style={{ background: "rgba(140, 34, 34, 0.10)", borderLeft: "4px solid var(--crimson-deep)" }} data-testid="module-still-open-panel">
              <div className="eyebrow mb-3" style={{ color: "var(--crimson-deep)" }}>Still Open · Parked, Not Blocking</div>
              <ul className="prose-paper">
                {module.still_open.map((s, i) => (
                  <li key={i} data-testid={`still-open-item-${i}`}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{s}</ReactMarkdown>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </article>
  );
}

// ============ DRAFTER ============
function DrafterView({ onSaved }) {
  const [raw, setRaw] = useState("");
  const [workingTitle, setWorkingTitle] = useState("");
  const [cleaned, setCleaned] = useState("");
  const [detectedOpen, setDetectedOpen] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const clean = async () => {
    if (!raw.trim()) return;
    setLoading(true); setError(""); setCleaned(""); setDetectedOpen([]);
    try {
      const { data } = await axios.post(`${API}/drafter`, {
        raw_notes: raw,
        module_title: workingTitle,
      });
      setCleaned(data.cleaned_markdown);
      setDetectedOpen(data.detected_still_open || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(cleaned);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="fade-up" data-testid="rules-drafter-workspace">
      <div className="mb-6">
        <div className="eyebrow" style={{ color: "var(--crimson-deep)" }}>Workshop · Rules Drafter</div>
        <h2 className="serif text-3xl md:text-4xl font-bold mt-1" style={{ color: "#F0E6D2" }}>The Typewriter</h2>
        <p className="serif-italic text-lg mt-2" style={{ color: "var(--paper-warm)" }}>
          Feed me raw notes. I'll match your locked-draft voice — em-dashes, numbered sections, worked examples, still-open flags.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="leather rounded-md p-6">
          <div className="eyebrow mb-3">Raw Notes</div>
          <input
            className="input-field mb-3"
            placeholder="Working title (optional) — e.g. Chair Building"
            value={workingTitle}
            onChange={(e) => setWorkingTitle(e.target.value)}
            data-testid="drafter-title-input"
          />
          <textarea
            className="input-field"
            style={{ minHeight: "420px" }}
            placeholder="Paste raw handwritten-style notes here. The Drafter will rewrite them in your locked voice…"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            data-testid="drafter-raw-textarea"
          />
          <div className="flex gap-3 mt-4">
            <button
              className="brass-btn-solid brass-btn flex items-center gap-2"
              disabled={loading || !raw.trim()}
              onClick={clean}
              data-testid="drafter-clean-btn"
            >
              <Sparkles size={13} />
              {loading ? "Drafting…" : "Clean into Locked Voice"}
            </button>
            {cleaned && (
              <button className="brass-btn" onClick={() => setSaveOpen(true)} data-testid="drafter-save-btn">
                Save as Module
              </button>
            )}
          </div>
          {error && <div className="mt-3 crimson-tag" data-testid="drafter-error">{error}</div>}
        </div>

        <div className="paper rounded-md p-6 md:p-8" style={{ minHeight: "500px" }}>
          <div className="eyebrow mb-3" style={{ color: "var(--crimson-deep)" }}>Cleaned Draft</div>
          {!cleaned && !loading && (
            <div className="serif-italic text-lg" style={{ color: "var(--muted)" }}>
              The manuscript will appear here after the Drafter runs.
            </div>
          )}
          {loading && (
            <div className="eyebrow" style={{ color: "var(--crimson-deep)" }}>Setting the type…</div>
          )}
          {cleaned && (
            <>
              <div className="flex justify-end mb-2">
                <button className="brass-btn text-xs" onClick={copy} data-testid="drafter-copy-btn">
                  {copied ? <><Check size={11} className="inline mr-1"/> Copied</> : <><Copy size={11} className="inline mr-1"/> Copy MD</>}
                </button>
              </div>
              <div className="prose-paper" data-testid="drafter-preview">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleaned}</ReactMarkdown>
              </div>
            </>
          )}
        </div>
      </div>

      {saveOpen && (
        <SaveDraftModal
          cleaned={cleaned}
          detectedOpen={detectedOpen}
          defaultTitle={workingTitle}
          onClose={() => setSaveOpen(false)}
          onSaved={async () => { setSaveOpen(false); setRaw(""); setCleaned(""); setWorkingTitle(""); await onSaved(); }}
        />
      )}
    </div>
  );
}

function SaveDraftModal({ cleaned, detectedOpen, defaultTitle, onClose, onSaved }) {
  const [title, setTitle] = useState(defaultTitle || "");
  const [slug, setSlug] = useState((defaultTitle || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await axios.post(`${API}/modules`, {
        slug,
        title,
        subtitle: "Rulebook Module · Draft",
        content: cleaned,
        status: "draft",
        still_open: detectedOpen,
        order: 100,
      });
      await onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="save-modal">
      <div className="leather rounded-md p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h3 className="serif text-xl" style={{ color: "var(--brass)" }}>Save as Module</h3>
          <button onClick={onClose} className="text-[color:var(--muted)] hover:text-[color:var(--brass)]" data-testid="save-modal-close"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="eyebrow block mb-1">Title</label>
            <input className="input-field" value={title} onChange={e => setTitle(e.target.value)} data-testid="save-modal-title" />
          </div>
          <div>
            <label className="eyebrow block mb-1">Slug (URL id)</label>
            <input className="input-field" value={slug} onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-"))} data-testid="save-modal-slug" />
          </div>
          {err && <div className="crimson-tag">{err}</div>}
          <div className="flex gap-2 justify-end mt-4">
            <button className="brass-btn" onClick={onClose}>Cancel</button>
            <button className="brass-btn brass-btn-solid" onClick={save} disabled={saving || !slug || !title} data-testid="save-modal-confirm">
              {saving ? "Saving…" : "Save Module"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ PLAYTEST ============
function PlaytestView({ sessions, onReload }) {
  const [activeId, setActiveId] = useState(sessions[0]?.id || null);
  const active = useMemo(() => sessions.find(s => s.id === activeId), [sessions, activeId]);

  useEffect(() => {
    if (!activeId && sessions[0]) setActiveId(sessions[0].id);
  }, [sessions, activeId]);

  const newSession = async () => {
    const { data } = await axios.post(`${API}/sessions`, {
      title: `Playtest — ${new Date().toLocaleDateString()}`,
      players: [],
      shifts: [{ shift_number: 1, notes: "", rule_breaks: "" }],
      still_open_flags: [],
    });
    await onReload();
    setActiveId(data.id);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 fade-up" data-testid="playtest-log-view">
      <aside className="leather rounded-md p-4 h-fit lg:sticky lg:top-6">
        <button className="brass-btn brass-btn-solid w-full mb-4 flex items-center justify-center gap-2" onClick={newSession} data-testid="new-session-btn">
          <Plus size={13} /> New Session
        </button>
        <div className="eyebrow mb-2 px-3">Sessions</div>
        {sessions.length === 0 && <div className="px-3 py-4 serif-italic text-sm" style={{ color: "var(--muted)" }}>No sessions yet. Log your first playtest.</div>}
        <div className="flex flex-col">
          {sessions.map(s => (
            <button
              key={s.id}
              className={`nav-item ${active?.id === s.id ? "active" : ""}`}
              onClick={() => setActiveId(s.id)}
              data-testid={`session-nav-${s.id}`}
            >
              <span className="status-dot" />
              <div className="flex flex-col items-start">
                <span className="truncate max-w-[200px]">{s.title}</span>
                <span style={{ fontSize: "10px", color: "var(--muted)", textTransform: "none", letterSpacing: 0 }}>
                  {new Date(s.date).toLocaleDateString()} · {(s.players || []).length}p
                </span>
              </div>
            </button>
          ))}
        </div>
      </aside>

      {active ? <SessionPane key={active.id} session={active} onReload={onReload} /> : (
        <div className="paper rounded-md p-8 fade-up delay-1">
          <div className="eyebrow" style={{ color: "var(--crimson-deep)" }}>Start Here</div>
          <h3 className="serif text-2xl mt-2" style={{ color: "var(--paper-ink)" }}>No session selected.</h3>
          <p className="serif-italic mt-2" style={{ color: "var(--paper-ink)" }}>Click <strong>New Session</strong> to open a fresh playtest ledger.</p>
        </div>
      )}
    </div>
  );
}

function SessionPane({ session, onReload }) {
  const [title, setTitle] = useState(session.title);
  const [playersRaw, setPlayersRaw] = useState((session.players || []).join(", "));
  const [shifts, setShifts] = useState(session.shifts || []);
  const [flagsRaw, setFlagsRaw] = useState((session.still_open_flags || []).join("\n"));
  const [summary, setSummary] = useState(session.summary || "");
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    setTitle(session.title);
    setPlayersRaw((session.players || []).join(", "));
    setShifts(session.shifts || []);
    setFlagsRaw((session.still_open_flags || []).join("\n"));
    setSummary(session.summary || "");
  }, [session.id]);

  const updateShift = (idx, patch) => {
    setShifts(shifts.map((s, i) => i === idx ? { ...s, ...patch } : s));
  };
  const addShift = () => {
    const next = shifts.length + 1;
    setShifts([...shifts, { shift_number: next, notes: "", rule_breaks: "" }]);
  };
  const removeShift = (idx) => setShifts(shifts.filter((_, i) => i !== idx));

  const save = async () => {
    await axios.patch(`${API}/sessions/${session.id}`, {
      title,
      players: playersRaw.split(",").map(s => s.trim()).filter(Boolean),
      shifts,
      still_open_flags: flagsRaw.split("\n").map(s => s.trim()).filter(Boolean),
      summary,
    });
    await onReload();
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1400);
  };

  const del = async () => {
    if (!window.confirm(`Delete session "${session.title}"?`)) return;
    await axios.delete(`${API}/sessions/${session.id}`);
    await onReload();
  };

  return (
    <article className="paper rounded-md p-8 fade-up delay-1" data-testid="session-active-pane">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="eyebrow" style={{ color: "var(--crimson-deep)" }}>Playtest Ledger · {new Date(session.date).toLocaleDateString()}</div>
          <input
            className="input-field mt-2 serif text-2xl"
            style={{ background: "rgba(197,160,89,0.08)", color: "var(--paper-ink)", borderColor: "var(--border-paper)" }}
            value={title}
            onChange={e => setTitle(e.target.value)}
            data-testid="session-title-input"
          />
        </div>
        <div className="flex gap-2">
          <button className="brass-btn" onClick={del} data-testid="session-delete-btn" style={{ color: "var(--crimson)" }}><Trash2 size={12} /></button>
          <button className="brass-btn brass-btn-solid" onClick={save} data-testid="session-save-btn">
            <Save size={12} className="inline mr-1" /> {savedFlash ? "Saved ✓" : "Save"}
          </button>
        </div>
      </div>

      <div className="ornament-divider"><span>❦</span></div>

      <div className="space-y-6">
        <div>
          <label className="eyebrow block mb-1" style={{ color: "var(--crimson-deep)" }}>Players (comma separated)</label>
          <input
            className="input-field"
            style={{ background: "rgba(197,160,89,0.08)", color: "var(--paper-ink)", borderColor: "var(--border-paper)" }}
            value={playersRaw}
            onChange={e => setPlayersRaw(e.target.value)}
            placeholder="Ada, Ben, Cass, Dev"
            data-testid="session-players-input"
          />
        </div>

        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="eyebrow" style={{ color: "var(--crimson-deep)" }}>Shifts</label>
            <button className="brass-btn text-xs" onClick={addShift} data-testid="add-shift-btn">
              <Plus size={11} className="inline mr-1" /> Add Shift
            </button>
          </div>
          <div className="space-y-4">
            {shifts.map((s, i) => (
              <div key={i} className="p-4 rounded" style={{ background: "rgba(197, 160, 89, 0.10)", border: "1px dashed var(--border-paper)" }} data-testid={`shift-block-${i}`}>
                <div className="flex justify-between items-center mb-2">
                  <span className="serif font-bold text-lg" style={{ color: "var(--paper-ink)" }}>Shift {s.shift_number}</span>
                  <button className="text-xs" onClick={() => removeShift(i)} style={{ color: "var(--crimson-deep)" }} data-testid={`remove-shift-${i}`}><Trash2 size={12} /></button>
                </div>
                <label className="eyebrow block mb-1" style={{ color: "var(--crimson-deep)" }}>Notes</label>
                <textarea
                  className="input-field mb-2"
                  style={{ background: "#FBF4E4", color: "var(--paper-ink)", borderColor: "var(--border-paper)", minHeight: "80px" }}
                  value={s.notes}
                  onChange={e => updateShift(i, { notes: e.target.value })}
                  placeholder="What happened this shift? Who took what slice?"
                  data-testid={`shift-notes-${i}`}
                />
                <label className="eyebrow block mb-1" style={{ color: "var(--crimson-deep)" }}>Rule Breaks / Confusions</label>
                <textarea
                  className="input-field"
                  style={{ background: "#FBF4E4", color: "var(--paper-ink)", borderColor: "var(--border-paper)", minHeight: "60px" }}
                  value={s.rule_breaks}
                  onChange={e => updateShift(i, { rule_breaks: e.target.value })}
                  placeholder="Anything unclear? Any rules bent or missed?"
                  data-testid={`shift-breaks-${i}`}
                />
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="eyebrow block mb-1" style={{ color: "var(--crimson-deep)" }}>Still Open Flags Raised (one per line)</label>
          <textarea
            className="input-field"
            style={{ background: "rgba(197,160,89,0.08)", color: "var(--paper-ink)", borderColor: "var(--border-paper)", minHeight: "100px" }}
            value={flagsRaw}
            onChange={e => setFlagsRaw(e.target.value)}
            data-testid="session-flags-textarea"
          />
        </div>

        <div>
          <label className="eyebrow block mb-1" style={{ color: "var(--crimson-deep)" }}>Session Summary</label>
          <textarea
            className="input-field"
            style={{ background: "rgba(197,160,89,0.08)", color: "var(--paper-ink)", borderColor: "var(--border-paper)", minHeight: "80px" }}
            value={summary}
            onChange={e => setSummary(e.target.value)}
            placeholder="Overall take. What worked, what didn't."
            data-testid="session-summary-textarea"
          />
        </div>
      </div>
    </article>
  );
}

// ============ STILL OPEN ============
function StillOpenView({ items }) {
  const [filter, setFilter] = useState("all");
  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter(i => i.source_type === filter);
  }, [items, filter]);

  const groupedBySource = useMemo(() => {
    const g = {};
    for (const i of filtered) {
      const key = `${i.source_type}:${i.source_slug}`;
      if (!g[key]) g[key] = { title: i.source_title, type: i.source_type, slug: i.source_slug, items: [] };
      g[key].items.push(i.text);
    }
    return Object.values(g);
  }, [filtered]);

  return (
    <div className="fade-up" data-testid="still-open-aggregator-vault">
      <div className="flex justify-between items-center flex-wrap gap-4 mb-6">
        <div>
          <div className="eyebrow" style={{ color: "var(--crimson-deep)" }}>Pinboard · Case Ambiguities</div>
          <h2 className="serif text-3xl md:text-4xl font-bold mt-1" style={{ color: "#F0E6D2" }}>Still Open</h2>
          <p className="serif-italic text-lg mt-2" style={{ color: "var(--paper-warm)" }}>
            Every parked question across modules and playtests. Resolve here, or leave for the next inquiry.
          </p>
        </div>
        <div className="flex gap-2">
          {[{k:"all",l:"All"},{k:"module",l:"From Rulebook"},{k:"session",l:"From Playtests"}].map(o => (
            <button
              key={o.k}
              className={`brass-btn ${filter === o.k ? "brass-btn-solid" : ""}`}
              onClick={() => setFilter(o.k)}
              data-testid={`filter-${o.k}`}
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="paper rounded-md p-8 fade-up delay-1">
          <div className="serif-italic text-lg" style={{ color: "var(--paper-ink)" }}>
            Nothing pending. Every module is locked. — <strong>Or you haven't flagged anything yet.</strong>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {groupedBySource.map((g, idx) => (
            /* "Slice & Ante" — After players work together to drag the rug (two players spend their turn other than a domino donation to "drag the rug" from the Lounge to the Banquet Hall), build a chair (4 donated dominoes shaped like an "h" atop the rug), another player drags the rug + chair to the Pantry, and if no other players are on it, this player climbs up and takes a well-earned slice — with others allowed to take the diametrically opposite slice at the cost of 1 domino. */
            <div key={idx} className="leather rounded-md p-5" data-testid={`still-open-group-${g.slug}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className={g.type === "module" ? "brass-tag" : "crimson-tag"}>{g.type === "module" ? "Module" : "Playtest"}</span>
                <span className="serif text-lg" style={{ color: "var(--brass)" }}>{g.title}</span>
              </div>
              <ul className="prose-leather">
                {g.items.map((t, i) => (
                  <li key={i} data-testid={`aggregated-open-${g.slug}-${i}`}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{t}</ReactMarkdown>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default App;
